import { PAYLINES } from '../core/paylines';
import { SEASON_2_PAYOUTS } from '../core/payout-tables';
import type { Grid, GridPosition, MoneyCents } from '../core/types';
import { evaluateWins } from '../core/wins';
import type { LegacyReplayLineDecoder, ReplayLineWinV2 } from './schema';

/**
 * V1 stored no line records. This adapter evaluates the stored grid exactly once while decoding,
 * checks win/no-win plausibility, and scales sub-cent Legacy totals deterministically to cents.
 * Playback itself receives only the resulting immutable records.
 */
export const LEGACY_V1_LINE_DECODER: LegacyReplayLineDecoder = Object.freeze({
  decodeStoredResult(input: Readonly<{
    grid: Grid;
    betCents: MoneyCents;
    spinWinCents: MoneyCents;
    stickyPositions?: readonly GridPosition[];
  }>): readonly ReplayLineWinV2[] | null {
    const { grid, betCents, spinWinCents, stickyPositions = [] } = input;
    if (!Number.isSafeInteger(spinWinCents) || spinWinCents < 0) return null;
    const stickyKeys = new Set(stickyPositions.map(({ reel, row }) => `${reel}:${row}`));
    const evaluationGrid = grid.map((column, reel) => column.map((symbol, row) => (
      stickyKeys.has(`${reel}:${row}`) ? 'WILD' : symbol
    ))) as unknown as Grid;
    const evaluation = evaluateWins(evaluationGrid, betCents, SEASON_2_PAYOUTS);
    if ((evaluation.totalWinCents === 0) !== (spinWinCents === 0)) return null;
    if (evaluation.lineWins.length === 0) return Object.freeze([]);

    let distributed = 0;
    return Object.freeze(evaluation.lineWins.map((line, index) => {
      const isLast = index === evaluation.lineWins.length - 1;
      const proportional = isLast
        ? spinWinCents - distributed
        : Math.floor((line.amountCents * spinWinCents) / evaluation.totalWinCents);
      distributed += proportional;
      return Object.freeze({
        lineIndex: line.lineIndex,
        route: PAYLINES[line.lineIndex]!,
        count: line.count,
        symbolId: line.symbolId,
        winCents: proportional,
      });
    }));
  },
});
